import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import { Button, Spinner } from "react-bootstrap";
import {
  format,
  startOfMonth,
  getDay,
  getDaysInMonth,
  addMonths,
  subMonths,
  getYear,
  getMonth,
} from "date-fns";
import config from "../../../../config";
import { toast } from "react-toastify";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const YEAR_OPTIONS = Array.from({ length: 16 }, (_, i) => 2020 + i);

const toastCfg = { theme: "colored", position: "top-center", autoClose: 2000, hideProgressBar: true };

const InventoryCalendarModal = ({
  open,
  onClose,
  mandapId,
  mandapName,
  stateName,
  tokenHeaders,
  onSaved,
}) => {
  const axiosCfg = { headers: tokenHeaders };

  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDates, setSelectedDates] = useState([]);
  const [existingIds, setExistingIds] = useState({});
  const [muhurtham, setMuhurtham] = useState({ valarpirai: [], theipirai: [] });
  const [loadingDates, setLoadingDates] = useState(false);
  const [saving, setSaving] = useState(false);
  const [muhurthamYear, setMuhurthamYear] = useState(null);
  const [hoveredDay, setHoveredDay] = useState(null);
  const [confirmClearAll, setConfirmClearAll] = useState(false);

  const year = getYear(currentDate);
  const month = getMonth(currentDate);

  const fetchInventoryDates = useCallback(() => {
    if (!mandapId) return;
    setLoadingDates(true);
    axios
      .get(`${config.service}/masters/mandap_inventory/?mandap_id=${mandapId}`, axiosCfg)
      .then((res) => {
        const entries = Array.isArray(res.data) ? res.data : res.data.results || [];
        const ids = {};
        entries.forEach((e) => { ids[e.date] = e.id; });
        setSelectedDates(Object.keys(ids));
        setExistingIds(ids);
        setLoadingDates(false);
      })
      .catch(() => setLoadingDates(false));
  }, [mandapId]);

  const fetchMuhurtham = useCallback(
    (yr) => {
      if (!stateName) return;
      axios
        .get(
          `${config.service}/booking/muhurtham_calendar?state=${encodeURIComponent(stateName)}&year=${yr}`,
          axiosCfg
        )
        .then((res) => {
          const data = res.data?.data?.[0];
          setMuhurtham({
            valarpirai: data?.valarpirai_dates || [],
            theipirai: data?.theipirai_dates || [],
          });
          setMuhurthamYear(yr);
        })
        .catch(() => {});
    },
    [stateName]
  );

  useEffect(() => {
    if (open) {
      fetchInventoryDates();
    } else {
      // reset on close
      setSelectedDates([]);
      setExistingIds({});
      setMuhurtham({ valarpirai: [], theipirai: [] });
      setMuhurthamYear(null);
      setCurrentDate(new Date());
    }
  }, [open, mandapId]);

  useEffect(() => {
    if (open && year !== muhurthamYear) {
      fetchMuhurtham(year);
    }
  }, [open, year]);

  const buildCalendarDays = () => {
    const firstDay = getDay(startOfMonth(currentDate));
    const total = getDaysInMonth(currentDate);
    const days = Array(firstDay).fill(null);
    for (let d = 1; d <= total; d++) days.push(d);
    return days;
  };

  const toggleDate = (dayNum) => {
    const ds = format(new Date(year, month, dayNum), "yyyy-MM-dd");
    setSelectedDates((prev) =>
      prev.includes(ds) ? prev.filter((d) => d !== ds) : [...prev, ds]
    );
  };

  const deleteDate = (ds) => {
    const id = existingIds[ds];
    if (id) {
      axios
        .delete(`${config.service}/masters/mandap_inventory/${id}/`, axiosCfg)
        .then(() => {
          setSelectedDates((prev) => prev.filter((d) => d !== ds));
          setExistingIds((prev) => {
            const next = { ...prev };
            delete next[ds];
            return next;
          });
          toast.success("Date removed", toastCfg);
          if (onSaved) onSaved();
        })
        .catch(() => toast.error("Failed to remove date", toastCfg));
    } else {
      setSelectedDates((prev) => prev.filter((d) => d !== ds));
    }
  };

  const getNewDates = () => selectedDates.filter((d) => !existingIds[d]);

  const hasExistingDates = Object.keys(existingIds).length > 0;
  const allDatesCleared = selectedDates.length === 0 && hasExistingDates;

  const saveNewDates = () => {
    const newDates = getNewDates();
    if (allDatesCleared) {
      setConfirmClearAll(true);
      return;
    }
    if (newDates.length === 0) {
      toast.info("No new dates to save", toastCfg);
      return;
    }
    setSaving(true);
    axios
      .post(
        `${config.service}/masters/mandap_inventory/bulk/`,
        { mandap_id: mandapId, dates: newDates, is_available: true },
        axiosCfg
      )
      .then((res) => {
        const count = res.data?.created_count ?? newDates.length;
        toast.success(`${count} date(s) saved successfully`, toastCfg);
        setSaving(false);
        fetchInventoryDates();
        if (onSaved) onSaved();
      })
      .catch((err) => {
        if (err.response?.status === 401) window.location.href = "/";
        toast.error("Failed to save dates", toastCfg);
        setSaving(false);
      });
  };

  const handleClearAll = () => {
    setConfirmClearAll(false);
    setSaving(true);
    axios
      .patch(
        `${config.service}/masters/vendor_mandap/${mandapId}/`,
        { is_inventory_purchased: false, dates: [] },
        axiosCfg
      )
      .then(() => {
        toast.success("All inventory dates cleared", toastCfg);
        setSaving(false);
        setExistingIds({});
        setSelectedDates([]);
        if (onSaved) onSaved();
        onClose();
      })
      .catch((err) => {
        if (err.response?.status === 401) window.location.href = "/";
        toast.error("Failed to clear inventory", toastCfg);
        setSaving(false);
      });
  };

  const getDayMeta = (ds) => ({
    isSelected: selectedDates.includes(ds),
    isExisting: !!existingIds[ds],
    isValarpirai: muhurtham.valarpirai.includes(ds),
    isTheipirai: muhurtham.theipirai.includes(ds),
  });

  const getDayCellStyle = (ds, isHovered) => {
    const { isSelected, isValarpirai, isTheipirai } = getDayMeta(ds);
    const base = {
      padding: "8px 0",
      borderRadius: "6px",
      cursor: "pointer",
      fontSize: "13px",
      userSelect: "none",
      textAlign: "center",
      transition: "background 0.12s",
    };
    if (isSelected) {
      return {
        ...base,
        background: "#28a745",
        color: "#fff",
        fontWeight: 700,
        outline: isValarpirai
          ? "3px solid #9b59b6"
          : isTheipirai
          ? "3px solid #3498db"
          : "none",
        outlineOffset: "1px",
      };
    }
    if (isValarpirai) return { ...base, background: "#9b59b6", color: "#fff" };
    if (isTheipirai) return { ...base, background: "#3498db", color: "#fff" };
    if (isHovered) return { ...base, background: "#e9ecef" };
    return base;
  };

  const days = buildCalendarDays();
  const newDates = getNewDates();

  const navBtnStyle = {
    background: "none",
    border: "1px solid #ccc",
    borderRadius: 4,
    padding: "3px 10px",
    cursor: "pointer",
    fontSize: "14px",
  };

  const selectStyle = {
    padding: "4px 8px",
    borderRadius: 4,
    border: "1px solid #ccc",
    fontSize: "13px",
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle style={{ borderBottom: "1px solid #dee2e6", paddingBottom: "10px" }}>
        Inventory Dates
        {mandapName ? ` — ${mandapName}` : mandapId ? ` — Mandap #${mandapId}` : ""}
      </DialogTitle>
      <DialogContent style={{ paddingTop: "16px" }}>
        {/* Legend */}
        <div
          style={{
            display: "flex",
            gap: "18px",
            marginBottom: "14px",
            flexWrap: "wrap",
            background: "#f8f9fa",
            borderRadius: 6,
            padding: "8px 12px",
          }}
        >
          {[
            { color: "#9b59b6", label: "Valarpirai (Muhurtham)" },
            { color: "#3498db", label: "Theipirai (Muhurtham)" },
            { color: "#28a745", label: "Selected / Saved" },
          ].map(({ color, label }) => (
            <span
              key={label}
              style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}
            >
              <span
                style={{
                  width: 14,
                  height: 14,
                  background: color,
                  borderRadius: 3,
                  display: "inline-block",
                  flexShrink: 0,
                }}
              />
              {label}
            </span>
          ))}
          <span style={{ fontSize: 12, color: "#666", marginLeft: "auto" }}>
            {stateName
              ? `Muhurtham: ${stateName}`
              : "No state — Muhurtham dates unavailable"}
          </span>
        </div>

        {/* Month / Year navigation */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            marginBottom: "14px",
            flexWrap: "wrap",
          }}
        >
          <select
            value={year}
            style={selectStyle}
            onChange={(e) =>
              setCurrentDate(new Date(parseInt(e.target.value), month, 1))
            }
          >
            {YEAR_OPTIONS.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <select
            value={month}
            style={selectStyle}
            onChange={(e) =>
              setCurrentDate(new Date(year, parseInt(e.target.value), 1))
            }
          >
            {MONTHS.map((m, i) => (
              <option key={i} value={i}>{m}</option>
            ))}
          </select>
          <button
            style={navBtnStyle}
            onClick={() => setCurrentDate((p) => subMonths(p, 1))}
          >
            ◀
          </button>
          <span style={{ fontWeight: 600, fontSize: "14px", minWidth: 140 }}>
            {MONTHS[month]} {year}
          </span>
          <button
            style={navBtnStyle}
            onClick={() => setCurrentDate((p) => addMonths(p, 1))}
          >
            ▶
          </button>
        </div>

        {/* Calendar grid */}
        {loadingDates ? (
          <div style={{ textAlign: "center", padding: "30px" }}>
            <CircularProgress size={28} />
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              gap: "4px",
            }}
          >
            {/* Day headers */}
            {DAYS.map((d) => (
              <div
                key={d}
                style={{
                  fontWeight: 700,
                  fontSize: "12px",
                  color: "#555",
                  textAlign: "center",
                  padding: "6px 0",
                  borderBottom: "1px solid #dee2e6",
                  marginBottom: "2px",
                }}
              >
                {d}
              </div>
            ))}

            {/* Day cells */}
            {days.map((day, i) => {
              if (!day) return <div key={`pad-${i}`} />;
              const ds = format(new Date(year, month, day), "yyyy-MM-dd");
              const isHov = hoveredDay === ds;
              return (
                <div
                  key={ds}
                  style={getDayCellStyle(ds, isHov)}
                  onClick={() => toggleDate(day)}
                  onMouseEnter={() => setHoveredDay(ds)}
                  onMouseLeave={() => setHoveredDay(null)}
                  title={ds}
                >
                  {day}
                </div>
              );
            })}
          </div>
        )}

        {/* Selected dates chips */}
        {selectedDates.length > 0 && (
          <div
            style={{
              marginTop: "18px",
              borderTop: "1px solid #dee2e6",
              paddingTop: "12px",
            }}
          >
            <div
              style={{
                fontWeight: 600,
                marginBottom: "8px",
                fontSize: "13px",
                display: "flex",
                alignItems: "center",
                gap: "10px",
              }}
            >
              <span>Selected Dates ({selectedDates.length})</span>
              {newDates.length > 0 && (
                <span
                  style={{
                    fontSize: "12px",
                    background: "#fff3cd",
                    border: "1px solid #ffc107",
                    borderRadius: 4,
                    padding: "1px 8px",
                  }}
                >
                  {newDates.length} unsaved
                </span>
              )}
            </div>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "6px",
                maxHeight: "130px",
                overflowY: "auto",
              }}
            >
              {[...selectedDates].sort().map((ds) => (
                <span
                  key={ds}
                  style={{
                    background: existingIds[ds] ? "#d4edda" : "#fff3cd",
                    border: `1px solid ${existingIds[ds] ? "#28a745" : "#ffc107"}`,
                    borderRadius: "4px",
                    padding: "2px 8px",
                    fontSize: "12px",
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                    cursor: "default",
                  }}
                >
                  {ds}
                  <span
                    style={{
                      cursor: "pointer",
                      color: "#dc3545",
                      fontWeight: "bold",
                      lineHeight: 1,
                      fontSize: "14px",
                    }}
                    title="Remove"
                    onClick={() => deleteDate(ds)}
                  >
                    ×
                  </span>
                </span>
              ))}
            </div>
          </div>
        )}
      </DialogContent>

      <DialogActions style={{ borderTop: "1px solid #dee2e6", padding: "12px 20px" }}>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
        <Button
          variant={allDatesCleared ? "danger" : "primary"}
          onClick={saveNewDates}
          disabled={saving || (newDates.length === 0 && !allDatesCleared)}
        >
          {saving ? (
            <Spinner animation="border" size="sm" />
          ) : allDatesCleared ? (
            "Clear All & Disable Inventory"
          ) : (
            `Save ${newDates.length > 0 ? newDates.length + " " : ""}New Date${newDates.length !== 1 ? "s" : ""}`
          )}
        </Button>
      </DialogActions>

      {/* Confirm clear all */}
      {confirmClearAll && (
        <Dialog open={confirmClearAll} onClose={() => setConfirmClearAll(false)} maxWidth="xs">
          <DialogTitle>Confirm Clear All</DialogTitle>
          <DialogContent>
            <div>
              This will set <strong>is_inventory = false</strong> and clear all dates for this mandap. Continue?
            </div>
          </DialogContent>
          <DialogActions>
            <Button variant="danger" onClick={handleClearAll} disabled={saving}>
              {saving ? <Spinner animation="border" size="sm" /> : "Confirm"}
            </Button>
            <Button variant="secondary" onClick={() => setConfirmClearAll(false)}>
              Cancel
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Dialog>
  );
};

export default InventoryCalendarModal;
